// Structured data (JSON-LD) builders for the non-feature public marketing
// pages (home/landing and pricing). Feature-page builders live alongside the
// catalog in `feature-catalog.ts`.

const SITE_URL = "https://padpulse.io";
const LOGO_URL = "https://padpulse.io/og-image.jpg";

import { PRICING } from "@shared/pricing";
import { pricingFaq, type FaqItem } from "@/data/pricing-faq";
import { HOME_SEO } from "@/data/marketing-seo";

type JsonLd = Record<string, unknown>;

function breadcrumbList(crumbs: { name: string; url: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

/**
 * Structured data for the landing/home page (/): an Organization entry, a
 * WebSite entry, and a SoftwareApplication entry describing the product.
 */
export function buildLandingJsonLd(): JsonLd[] {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "PadPulse",
      url: SITE_URL,
      logo: LOGO_URL,
      description:
        "PadPulse is operations software for PadSplit hosts and co-living operators. It connects to a host's PadSplit account and puts member messages, booking requests, maintenance, smart locks and thermostats, and a live P&L per property in one place.",
      email: "info@padpulse.io",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "PadPulse",
      url: SITE_URL,
    },
    softwareApplication(),
  ];
}

/** Host offer: the first tier's per-property monthly price (e.g. $49 per property per month). */
function hostOffer(name: string, description: string): JsonLd {
  const base = PRICING.hostTiersMonthly[0].unitAmount / 100;
  return {
    "@type": "Offer",
    name,
    description,
    price: String(base),
    priceCurrency: "USD",
    url: `${SITE_URL}/pricing`,
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: String(base),
      priceCurrency: "USD",
      unitText: "property per month",
      referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "property" },
      billingDuration: "P1M",
    },
  };
}

/** SoftwareApplication with offers built from `@shared/pricing` (Research free, Host and Property Manager per property). */
export function softwareApplication(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "PadPulse",
    description: HOME_SEO.description,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: SITE_URL,
    offers: [
      {
        "@type": "Offer",
        name: "Research",
        description: `Free research tools: ${PRICING.research.freeSearchesPerMonth} buy box searches a month, plus market research and underwriting.`,
        price: "0",
        priceCurrency: "USD",
        url: `${SITE_URL}/pricing`,
      },
      hostOffer("Host", "Host tools priced per property per month. AI-drafted replies require beta access; continuous Auto Draft must be enabled."),
      hostOffer(
        "Property Manager",
        `Host pricing per property, plus $${PRICING.ownerPortal.monthlyPerOwner / 100} per owner per month for the owner portal and monthly owner statements.`,
      ),
    ],
    publisher: {
      "@type": "Organization",
      name: "PadPulse",
      url: SITE_URL,
    },
  };
}

export function faqPage(items: FaqItem[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

/**
 * Structured data for the pricing page (/pricing): a breadcrumb trail
 * (Home › Pricing), a SoftwareApplication whose offers come from
 * `@shared/pricing`, and an FAQPage of the pricing FAQ.
 */
export function buildPricingJsonLd(now: Date = new Date()): JsonLd[] {
  const pricingUrl = `${SITE_URL}/pricing`;
  return [
    breadcrumbList([
      { name: "Home", url: SITE_URL },
      { name: "Pricing", url: pricingUrl },
    ]),
    softwareApplication(),
    faqPage(pricingFaq(now)),
  ];
}
